import React, { useCallback, useEffect, useRef, useState } from "react";
import InfoDot from "../components/whats-new/InfoDot";
import PostCard from "../components/whats-new/PostCard";
import {
  UnreadDivider,
  EarlierDivider,
  EmptyState,
  FeedSkeleton,
  LoadMoreButton,
} from "../components/whats-new/FeedStates";
import { whatsNewService } from "../services/whatsNewService";

// What's New — the platform's official update feed (Content menu). Newest
// first, pinned on top, a "New since your last visit" divider driven by the
// per-user last-visit marker, category chips, read-more, load-more, curated
// reactions and polls. Audience targeting is entirely server-side and invisible
// here. Copy is English.
//
// Responsive rule (Marin, explicit): the page lives in the platform layout; the
// reading column is capped + centred (no fixed prototype width) so it scales on
// wide displays and degrades to a single column on phones. Design 1a reading
// column: 660px; mobile side padding 14px (1f).

const INFO_TEXT =
  "Official updates, feature launches and quick polls from the TransferPitch team. React to a post or cast a vote. That's all the feedback we need.";

// A post is "unread" when it was published after the reader's previous visit.
function isUnread(post, previousVisit) {
  if (!previousVisit) return false;
  const pub = new Date(post.published_at).getTime();
  const prev = new Date(previousVisit).getTime();
  return !Number.isNaN(pub) && !Number.isNaN(prev) && pub > prev;
}

const WhatsNew = () => {
  const [posts, setPosts] = useState([]);
  const [nextPage, setNextPage] = useState(null); // next page number or null
  const [loading, setLoading] = useState(true); // initial load
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [previousVisit, setPreviousVisit] = useState(undefined); // undefined=pending
  const didInit = useRef(false);

  // Stamp the visit marker once on open. The response carries the PREVIOUS
  // marker, which positions the unread divider for THIS render; it also clears
  // the menu dot (event listened to by the header nav hook).
  useEffect(() => {
    // didInit dedupes the StrictMode double-invocation. No stale-closure guard
    // here: its cleanup runs between the two invocations and would permanently
    // swallow the response (divider + visited event never fired in dev).
    // React 18 makes setState after unmount a safe no-op.
    if (didInit.current) return;
    didInit.current = true;
    whatsNewService.visit().then((res) => {
      setPreviousVisit(res.success ? res.previousVisit : null);
      window.dispatchEvent(new Event("whatsnew:visited"));
    });
  }, []);

  // Initial feed load (page 1).
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await whatsNewService.getPosts(1, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (res.success) {
          setPosts(res.results);
          setNextPage(res.next ? 2 : null);
        } else {
          setError(res.error || "Failed to load updates");
        }
      } catch (e) {
        if (e?.name === "CanceledError" || ctrl.signal.aborted) return;
        setError("Failed to load updates");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    const res = await whatsNewService.getPosts(nextPage);
    setLoadingMore(false);
    if (res.success) {
      setPosts((prev) => [...prev, ...res.results]);
      setNextPage(res.next ? nextPage + 1 : null);
    }
  }, [nextPage, loadingMore]);

  // Index of the first non-pinned unread post → the divider sits just above it.
  // Pinned posts always render on top (above the divider), matching the design.
  const dividerIndex = (() => {
    if (!previousVisit) return -1;
    return posts.findIndex((p) => !p.is_pinned && isUnread(p, previousVisit));
  })();

  // "EARLIER UPDATES" (design 1a) closes the unread block: it sits above the
  // first already-seen post AFTER the unread divider. Derived from the same
  // last-visit marker — rendered only while the unread divider is shown.
  const earlierIndex = (() => {
    if (dividerIndex < 0) return -1;
    return posts.findIndex(
      (p, i) => i > dividerIndex && !p.is_pinned && !isUnread(p, previousVisit),
    );
  })();

  return (
    <div className="w-full min-h-full bg-[#F2F5F9]">
      <div className="max-w-[700px] mx-auto px-3.5 sm:px-5 pt-4 sm:pt-10 pb-10 sm:pb-14">
        {/* Title + info dot (popover on desktop, bottom sheet on mobile) */}
        <div className="flex items-center gap-2 sm:gap-2.5">
          <h1 className="text-[23px] sm:text-[29px] font-extrabold tracking-[-0.02em] text-[#101B2C]">
            What's New
          </h1>
          <InfoDot text={INFO_TEXT} sheetTitle="About What's New" confirmLabel="Got it" />
        </div>

        <div className="mt-2.5 sm:mt-[26px]">
          {loading ? (
            <FeedSkeleton />
          ) : error ? (
            <div className="bg-white border border-[#E7EBF1] rounded-2xl py-12 px-6 text-center">
              <p className="text-[15px] font-semibold text-[#101B2C]">
                Couldn't load updates
              </p>
              <p className="mt-1.5 text-[13.5px] text-[#7C8CA1]">
                Please try again in a moment.
              </p>
            </div>
          ) : posts.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="flex flex-col gap-3.5 sm:gap-4">
              {posts.map((post, idx) => (
                <React.Fragment key={post.id}>
                  {idx === dividerIndex && <UnreadDivider />}
                  {idx === earlierIndex && <EarlierDivider />}
                  <PostCard post={post} />
                </React.Fragment>
              ))}
              {nextPage && (
                <LoadMoreButton loading={loadingMore} onClick={loadMore} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsNew;
